import React from 'react';
import { Modal } from 'antd';
import { FileText, CheckCircle } from 'lucide-react';

interface DPAModalProps {
  isOpen: boolean;
  onAccept: () => void;
  onDecline: () => void;
  loading?: boolean;
}

const DPAModal: React.FC<DPAModalProps> = ({ isOpen, onAccept, onDecline, loading = false }) => {
  return (
    <Modal
      open={isOpen}
      onCancel={onDecline}
      footer={null}
      width={700}
      centered
      maskClosable={false}
    >
      <div className="py-4">
        {/* Header */}
        <div className="flex items-center mb-6">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-4">
            <FileText className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Data Processing Agreement</h2>
            <p className="text-sm text-gray-600">Required to connect with Shopify</p>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-6 mb-6 bg-gray-50">
          <div className="space-y-4 text-sm text-gray-700">
            <div>
              <h3 className="font-semibold text-base text-gray-900 mb-2">1. Acceptance</h3>
              <p>
                By installing, accessing, or using the Adrsta.ai application, or by clicking "I Agree",
                you agree to this Data Processing Agreement ("DPA"), which forms part of the Adrsta.ai Terms of Service.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-base text-gray-900 mb-2">2. Roles of the Parties</h3>
              <p>
                You (the merchant) act as the <strong>Data Controller</strong> and determine the purposes
                and means of processing Personal Data. Adrsta.ai acts solely as a <strong>Data Processor</strong>
                and processes Personal Data only on your documented instructions.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-base text-gray-900 mb-2">3. Scope of Processing</h3>
              <p className="mb-2"><strong>Data Subjects:</strong> Your customers (merchant customers)</p>
              <p className="mb-2"><strong>Categories of Personal Data:</strong></p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Location attributes (state, city, country, DMA)</li>
                <li>Demographic attributes (age or age range, gender)</li>
                <li>Device information (device type, operating system, browser metadata)</li>
              </ul>
              <p className="mt-2">
                <strong>We do NOT store:</strong> names, email addresses, phone numbers, or payment data.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-base text-gray-900 mb-2">4. Purpose of Processing</h3>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Analytics and insights</li>
                <li>Aggregated reporting and segmentation</li>
                <li>Improving and operating the Adrsta.ai Services</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-base text-gray-900 mb-2">5. Your Rights</h3>
              <p>We assist you in responding to data subject requests including:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Access and correction</li>
                <li>Deletion</li>
                <li>Data export</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-base text-gray-900 mb-2">6. Security Measures</h3>
              <p>We implement industry-standard safeguards including:</p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                <li>Encryption in transit and at rest</li>
                <li>Role-based access controls</li>
                <li>Monitoring and audit logging</li>
                <li>Incident response procedures</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-base text-gray-900 mb-2">7. Data Retention</h3>
              <p>
                Personal Data is retained only as necessary to provide the Services. Upon termination
                or request, we will delete or return Personal Data unless legally required to retain it.
              </p>
            </div>

            {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm">
                <strong>Full DPA:</strong> The complete Data Processing Agreement with all appendices is available at{' '}
                <a
                  href="https://adrsta.ai/legal/dpa"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  https://adrsta.ai/legal/dpa
                </a>
              </p>
            </div> */}
          </div>
        </div>

        {/* Checkbox and Actions */}
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start">
              <CheckCircle className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-700">
                By clicking "I Agree", you confirm that you have read and agree to the Adrsta.ai
                Data Processing Agreement and Terms of Service.
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={onDecline}
              disabled={loading}
              className="flex-1 py-3 px-6 rounded-lg text-gray-700 font-semibold border-2 border-gray-300 hover:bg-gray-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Decline
            </button>
            <button
              onClick={onAccept}
              disabled={loading}
              className="flex-1 py-3 px-6 rounded-lg text-white font-semibold bg-green-600 hover:bg-green-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  I Agree
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DPAModal;
